/**
 * The mention vocabulary a text composer shares with every surface that writes `@` selections: how a
 * structured mention becomes a token of the text, how that token is found in stored prose, and the range
 * bookkeeping that keeps a selection attached to its token while the text around it is edited.
 *
 * One owner, because the daemon, the chat composer and a plugin's own editor must agree on the same
 * grammar: a token starts a word (so `jana@example.cz` stays an address) and ends one (so `@Janet` is not
 * `@Jana`). Offsets are half-open UTF-16 ranges of the original text, never of a label or of expanded
 * send text. Nothing here performs I/O, and the DOM helper (`markMentions`) touches only the document it
 * is handed.
 */

/** The name a mention token and a picker row show: the account's display name, else its username (as
 *  `Avatar` labels it). The composer writes a picked mention as `@` and this label, and the agent's turn
 *  context names every member by it, so it is what a stored message's mention reads as. */
export function mentionLabel(profile) {
  return (profile.name ?? '').trim() || profile.username;
}

/** What a token's chip slot is keyed by: the account id, or `agent`. */
export function mentionKey(token) {
  return token.kind === 'agent' ? 'agent' : String(token.person.id);
}

/**
 * The tokens a stored message's structured `mentions` may be written as, longest first so `@Jana Nováková`
 * wins over `@Jana`. A mention's stored name is what the writer saw when sending; the member's current
 * profile (from `people`, the conversation's owner and participants) adds the current label and supplies the
 * avatar. A mention of someone no longer in the list keeps its stored name and the initials. The agent's
 * mention reads as its stored name, the product name the composer inserts.
 */
export function mentionTokens(mentions, people) {
  const tokens = [];
  for (const mention of mentions ?? []) {
    if (mention.id === 'agent') {
      const label = mention.name.trim();
      if (label && !tokens.some((token) => token.label === label)) tokens.push({ kind: 'agent', label });
      continue;
    }
    const person = people?.get(mention.id) ?? { id: mention.id, name: mention.name, username: mention.name, avatar: null };
    for (const label of new Set([mention.name.trim(), mentionLabel(person)])) {
      if (label && !tokens.some((token) => token.label === label)) tokens.push({ kind: 'person', person, label });
    }
  }
  return tokens.sort((a, b) => b.label.length - a.label.length);
}

const WORD = /[\p{L}\p{M}\p{N}_]/u;
/** What may stand before the `@` of an e-mail address: its local part. A token after one of these is no mention. */
const ADDRESS_LOCAL = /[\p{L}\p{M}\p{N}_.%+\-]/u;

/** Cuts `text` at the known tokens. A token starts a word, as the composer's `@` must (so `jana@example.cz`
 *  stays an address), and ends one (so `@Janet` is not `@Jana`); text naming nobody among `tokens` stays plain. */
export function splitMentions(text, tokens) {
  const parts = [];
  if (!tokens.length) return [text];
  let plainFrom = 0;
  let at = text.indexOf('@');
  while (at >= 0) {
    const before = text[at - 1];
    const token = before !== undefined && ADDRESS_LOCAL.test(before) ? undefined : tokens.find(({ label }) => {
      const after = text[at + 1 + label.length];
      return text.startsWith(label, at + 1) && (after === undefined || !WORD.test(after));
    });
    if (token) {
      if (at > plainFrom) parts.push(text.slice(plainFrom, at));
      parts.push(token);
      plainFrom = at + 1 + token.label.length;
    }
    at = text.indexOf('@', token ? plainFrom : at + 1);
  }
  if (plainFrom < text.length) parts.push(text.slice(plainFrom));
  return parts;
}

/** The element a rendered Markdown document keeps for each token, filled with the host's chip. */
export const MENTION_SLOT = 'data-mention-slot';

/** Replaces the tokens in a rendered Markdown document's prose with empty slot elements carrying the account
 *  id and the label. Code, preformatted text and links stay as written: a mention there is quoted text. */
export function markMentions(root, tokens) {
  if (!tokens.length) return;
  // The document's own window, not this module's globals: a rendered document may come from another realm.
  // A DETACHED document has no window (`defaultView` is null, which is what the Markdown renderer hands
  // here), so its walker uses the platform's NodeFilter instead.
  const view = root.ownerDocument.defaultView;
  const walker = root.ownerDocument.createTreeWalker(root, (view?.NodeFilter ?? globalThis.NodeFilter).SHOW_TEXT);
  const texts = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!node.parentElement?.closest('code, pre, a')) texts.push(node);
  }
  for (const node of texts) {
    const parts = splitMentions(node.data, tokens);
    if (parts.every((part) => typeof part === 'string')) continue;
    node.replaceWith(...parts.map((part) => {
      if (typeof part === 'string') return part;
      const slot = root.ownerDocument.createElement('span');
      slot.setAttribute(MENTION_SLOT, mentionKey(part));
      slot.setAttribute('data-mention-label', part.label);
      return slot;
    }));
  }
}

/**
 * Cross-field policy for structurally validated composer selections. Callers validate nonempty tokens,
 * safe nonnegative integer offsets and their own collection bound before calling this function.
 * Offsets address the original text as half-open UTF-16 ranges, never labels or expanded send text.
 * Returns indexed issues without changing text, order, identities or metadata; it performs no I/O.
 */
export function composerMentionRangeIssues(text, mentions) {
  const issues = [];
  for (const [index, mention] of mentions.entries()) {
    if (mention.start >= mention.end || mention.end > text.length
      || text.slice(mention.start, mention.end) !== mention.token) {
      issues.push({ index, message: 'Mention range must select its exact token in composer text' });
    }
    if (mentions.slice(0, index).some(other => mention.start < other.end && other.start < mention.end)) {
      issues.push({ index, message: 'Mention ranges must not overlap' });
    }
  }
  return issues;
}

/** The same policy as a loud precondition: a selection whose token no longer sits at its range is a
 *  programming error in the caller, never a state to send or to render. */
export function assertComposerMentions(text, mentions) {
  for (const mention of mentions) {
    if (typeof mention.token !== 'string' || mention.token.length === 0 || !Number.isSafeInteger(mention.start)
      || !Number.isSafeInteger(mention.end) || mention.start < 0 || mention.end < 0) {
      throw new Error('Invalid composer mention range.');
    }
  }
  if (composerMentionRangeIssues(text, mentions).length) throw new Error('Invalid composer mention range.');
}

/** The `@` query being typed at the caret, or null. The `@` must start a word (text start or after
 *  whitespace), so an address or a mid-word `@` never opens the picker, and whitespace ends the query. */
export function activeMentionQuery(text, caret) {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(before[at - 1])) return null;
  const query = before.slice(at + 1);
  return /\s/.test(query) ? null : { start: at, query };
}

/** The one contiguous replacement that turns `previous` into `next`, or null when nothing changed. A
 *  `<textarea>` reports no target ranges for a deletion, so the observed change is the only honest statement of
 *  what an edit replaced: the widest contiguous range that explains it lets {@link applyMentionEdit} move every
 *  selection the edit does not touch, while a selection the range covers is dropped rather than guessed at. */
export function singleEdit(previous, next) {
  if (previous === next) return null;
  const shortest = Math.min(previous.length, next.length);
  let prefix = 0;
  while (prefix < shortest && previous[prefix] === next[prefix]) prefix++;
  let suffix = 0;
  while (suffix < shortest - prefix && previous[previous.length - 1 - suffix] === next[next.length - 1 - suffix]) suffix++;
  return { start: prefix, end: previous.length - suffix, inserted: next.slice(prefix, next.length - suffix) };
}

/** The selected identities a send carries, once each. */
export function mentionIdsIn(text, mentions) {
  assertComposerMentions(text, mentions);
  const ids = [];
  for (const mention of mentions) {
    if (!ids.includes(mention.id)) ids.push(mention.id);
  }
  return ids;
}

/**
 * Apply a known replacement to `text` and its selections: every range after the edit moves by its length
 * delta, a range the edit covers is dropped rather than guessed at, and a range it leaves alone is kept as
 * it is. Throws for an edit outside the text or for selections that are already invalid.
 */
export function applyMentionEdit(text, mentions, start, end, inserted) {
  if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || start < 0 || end < start || end > text.length) {
    throw new Error('Invalid composer edit range.');
  }
  assertComposerMentions(text, mentions);
  const delta = inserted.length - (end - start);
  const next = mentions.flatMap(mention => {
    if (end <= mention.start) return [{ ...mention, start: mention.start + delta, end: mention.end + delta }];
    if (start >= mention.end) return [mention];
    return [];
  });
  return { text: text.slice(0, start) + inserted + text.slice(end), mentions: next };
}

/**
 * Replace the typed `@query` at `start..end` with the account's (or the agent's) token and a space, and
 * record the mention. Returns the text, the selections and the caret position after the inserted space.
 */
export function insertMentionToken(text, mentions, start, end, account) {
  const token = `@${account.label}`;
  const edit = applyMentionEdit(text, mentions, start, end, `${token} `);
  return {
    text: edit.text,
    mentions: [...edit.mentions, { id: account.id, token, start, end: start + token.length }],
    caret: start + token.length + 1,
  };
}
